import { useEffect, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCreateTemplate } from "@/features/templates/hooks";
import type {
  TemplateDirection,
  TemplateDraft,
  TemplateVariable,
} from "@/features/templates/api";

export function emptyDraft(): TemplateDraft {
  return {
    name: "",
    description: "",
    category: "",
    direction: "inbound",
    system_prompt: "",
    greeting: "",
    variables: [],
    tools: [],
    flow: { nodes: [], edges: [] },
    rules: "",
    faq: [],
    source: "manual",
  };
}

/**
 * Review + save a template draft — the shared final step for New, Import,
 * and Generate-with-AI. Variables shown here are the ones auto-detected from
 * the source; the server re-derives the authoritative manifest on save.
 */
export default function DraftReviewDialog({
  open,
  draft,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  draft: TemplateDraft | null;
  onOpenChange: (v: boolean) => void;
  onCreated: (id: string) => void;
}) {
  const create = useCreateTemplate();
  const [form, setForm] = useState<TemplateDraft>(emptyDraft());

  useEffect(() => {
    if (draft) setForm(draft);
  }, [draft]);

  function set<K extends keyof TemplateDraft>(key: K, value: TemplateDraft[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  const userVars = form.variables.filter((v) => v.scope === "user");
  const systemVars = form.variables.filter((v) => v.scope === "system");

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Give the template a name");
      return;
    }
    try {
      const saved = await create.mutateAsync({
        name: form.name.trim(),
        description: form.description,
        category: form.category,
        direction: form.direction,
        system_prompt: form.system_prompt,
        greeting: form.greeting,
        tools: form.tools,
        flow: form.flow,
        rules: form.rules,
        faq: form.faq,
        source: form.source,
      });
      toast.success("Template saved to the library");
      onOpenChange(false);
      onCreated(saved.id);
    } catch (err) {
      const e = err as { response?: { data?: { error?: string; name?: string[] } } };
      toast.error(
        e?.response?.data?.error ||
          e?.response?.data?.name?.[0] ||
          "Failed to save template",
      );
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Review template</DialogTitle>
          <DialogDescription>
            Check the details, then save it to the library. You&apos;ll fill in
            the variables later when an agent adopts it.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="t-name">Name</Label>
              <Input
                id="t-name"
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                required
                autoFocus
              />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="t-desc">Description</Label>
              <Input
                id="t-desc"
                value={form.description}
                onChange={(e) => set("description", e.target.value)}
                placeholder="One line shown on the card"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="t-cat">Category</Label>
              <Input
                id="t-cat"
                value={form.category}
                onChange={(e) => set("category", e.target.value)}
                placeholder="healthcare, legal…"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Direction</Label>
              <Select
                value={form.direction}
                onValueChange={(v) => set("direction", v as TemplateDirection)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="inbound">Inbound (answers calls)</SelectItem>
                  <SelectItem value="outbound">Outbound (places calls)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="t-greeting">Greeting</Label>
            <Textarea
              id="t-greeting"
              rows={2}
              value={form.greeting}
              onChange={(e) => set("greeting", e.target.value)}
              placeholder="Opening line the agent speaks first"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="t-prompt">System prompt</Label>
            <Textarea
              id="t-prompt"
              rows={10}
              value={form.system_prompt}
              onChange={(e) => set("system_prompt", e.target.value)}
              className="font-mono text-xs"
              placeholder="Full prompt with {{variables}} left in place"
            />
          </div>

          <div className="flex flex-wrap gap-2 text-xs">
            <StructChip label="Flow steps" n={form.flow?.steps?.length ?? 0} />
            <StructChip label="FAQ entries" n={form.faq?.length ?? 0} />
            <StructChip label="Rules" n={form.rules?.length ? 1 : 0} suffix={form.rules ? "yes" : "none"} />
            <StructChip label="Greeting" n={form.greeting ? 1 : 0} suffix={form.greeting ? "yes" : "none"} />
          </div>

          <VariableSummary userVars={userVars} systemVars={systemVars} />

          {form.tools.length > 0 && (
            <div className="space-y-1.5">
              <Label>Tools referenced</Label>
              <div className="flex flex-wrap gap-1.5">
                {form.tools.map((t) => (
                  <span
                    key={t}
                    className="inline-flex items-center rounded-md bg-muted px-2 py-0.5 font-mono text-xs"
                  >
                    {t}
                  </span>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                These are declared only. Wiring tools to real actions comes in a
                later phase.
              </p>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={create.isPending || !form.name.trim()}>
              {create.isPending ? "Saving…" : "Save to library"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function StructChip({
  label,
  n,
  suffix,
}: {
  label: string;
  n: number;
  suffix?: string;
}) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium">{suffix ?? n}</span>
    </span>
  );
}

function VariableSummary({
  userVars,
  systemVars,
}: {
  userVars: TemplateVariable[];
  systemVars: TemplateVariable[];
}) {
  if (userVars.length === 0 && systemVars.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">
        No <code>{"{{variables}}"}</code> detected yet — add them to the prompt
        and they&apos;ll be picked up on save.
      </p>
    );
  }
  return (
    <div className="space-y-2 rounded-md border p-3">
      <div>
        <p className="text-xs font-medium">
          Fields to fill in ({userVars.length})
        </p>
        <div className="mt-1 flex flex-wrap gap-1.5">
          {userVars.map((v) => (
            <span
              key={v.name}
              className="inline-flex items-center rounded-md border px-2 py-0.5 font-mono text-xs"
              title={v.group}
            >
              {v.name}
            </span>
          ))}
        </div>
      </div>
      {systemVars.length > 0 && (
        <div>
          <p className="text-xs font-medium text-muted-foreground">
            Auto-filled by the platform ({systemVars.length})
          </p>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {systemVars.map((v) => (
              <span
                key={v.name}
                className="inline-flex items-center rounded-md bg-muted px-2 py-0.5 font-mono text-xs text-muted-foreground"
              >
                {v.name}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
